import type { AgentSession } from "@/services/types/agent-gateway";

export type SessionStatusCategory =
  | "in_progress"
  | "completed"
  | "failed"
  | "other";

export type SessionTimeFilter = "all" | "7d" | "30d";

export interface SessionStatusPresentation {
  category: SessionStatusCategory;
  label: string;
}

export interface SessionListFilters {
  query: string;
  status: string;
  time: SessionTimeFilter;
}

const IN_PROGRESS_STATUSES = new Set([
  "active",
  "in_progress",
  "processing",
  "running",
  "working",
]);
const COMPLETED_STATUSES = new Set([
  "complete",
  "completed",
  "succeeded",
  "success",
]);
const FAILED_STATUSES = new Set(["error", "failed", "failure"]);

function normalizeStatusValue(status: string) {
  return status
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function getStatusFilterValue(status: string) {
  const presentation = getSessionStatusPresentation(status);
  return presentation.category === "other"
    ? normalizeStatusValue(status)
    : presentation.category;
}

export function humanizeSessionStatus(status: string) {
  const normalized = normalizeStatusValue(status);

  if (!normalized) {
    return "Unknown";
  }

  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function getSessionStatusPresentation(
  status: string,
): SessionStatusPresentation {
  const normalized = normalizeStatusValue(status);

  if (IN_PROGRESS_STATUSES.has(normalized)) {
    return { category: "in_progress", label: "In progress" };
  }

  if (COMPLETED_STATUSES.has(normalized)) {
    return { category: "completed", label: "Completed" };
  }

  if (FAILED_STATUSES.has(normalized)) {
    return { category: "failed", label: "Failed" };
  }

  return { category: "other", label: humanizeSessionStatus(status) };
}

export function formatSessionDuration(session: AgentSession) {
  const createdAt = new Date(session.created_at).getTime();
  const updatedAt = new Date(session.updated_at).getTime();
  const elapsedSeconds = Math.max(
    0,
    Math.floor((updatedAt - createdAt) / 1000),
  );

  if (elapsedSeconds < 60) {
    return `${elapsedSeconds}s`;
  }

  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;

  if (minutes < 60) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export function formatSessionDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export function formatSessionDateTime(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export function getSessionMetrics(sessions: AgentSession[]) {
  return sessions.reduce(
    (metrics, session) => {
      const { category } = getSessionStatusPresentation(session.status);

      if (category === "in_progress") {
        metrics.inProgress += 1;
      }

      if (category === "failed") {
        metrics.failed += 1;
      }

      return metrics;
    },
    { total: sessions.length, inProgress: 0, failed: 0 },
  );
}

export function getSessionStatusOptions(sessions: AgentSession[]) {
  return Array.from(
    new Map(
      sessions.map((session) => {
        const presentation = getSessionStatusPresentation(session.status);
        return [getStatusFilterValue(session.status), presentation.label];
      }),
    ),
  )
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function filterAgentSessions(
  sessions: AgentSession[],
  filters: SessionListFilters,
  now = new Date(),
) {
  const normalizedQuery = filters.query.trim().toLocaleLowerCase();
  const maximumAge =
    filters.time === "7d" ? 7 : filters.time === "30d" ? 30 : null;
  const nowTime = now.getTime();

  return sessions
    .filter((session) => {
      if (
        filters.status !== "all" &&
        getStatusFilterValue(session.status) !== filters.status
      ) {
        return false;
      }

      if (maximumAge !== null) {
        const ageInMilliseconds =
          nowTime - new Date(session.created_at).getTime();
        const maximumAgeInMilliseconds = maximumAge * 24 * 60 * 60 * 1000;

        if (
          ageInMilliseconds < 0 ||
          ageInMilliseconds > maximumAgeInMilliseconds
        ) {
          return false;
        }
      }

      if (!normalizedQuery) {
        return true;
      }

      return [
        session.title,
        session.id,
        session.created_by.name ?? "",
        session.created_at,
        formatSessionDate(session.created_at),
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(normalizedQuery);
    })
    .sort(
      (left, right) =>
        new Date(right.created_at).getTime() -
        new Date(left.created_at).getTime(),
    );
}
